import { useState, useCallback, useEffect, useRef } from "react";
import type {
  Message,
  Session,
  ProviderName,
  TokenUsage,
} from "@cowork/shared";
import { PROVIDER_INFOS } from "@cowork/shared";
import { engineWS } from "../lib/ws.js";

function createMessage(
  role: Message["role"],
  text: string,
  provider?: ProviderName,
  model?: string,
  tokenUsage?: TokenUsage,
  fileCards?: Message["fileCards"],
): Message {
  return {
    id: crypto.randomUUID(),
    role,
    content: [{ type: "text", text }],
    createdAt: new Date().toISOString(),
    ...(provider !== undefined && { provider }),
    ...(model !== undefined && { model }),
    ...(tokenUsage !== undefined && { tokenUsage }),
    ...(fileCards && fileCards.length > 0 && { fileCards }),
  };
}

function estimateCost(provider: ProviderName, model: string, usage: TokenUsage): number {
  const info = PROVIDER_INFOS[provider];
  const modelInfo = info.models.find((m) => m.id === model);
  if (!modelInfo) return 0;
  return (
    (usage.inputTokens * modelInfo.inputCostPer1M +
      usage.outputTokens * modelInfo.outputCostPer1M) /
    1_000_000
  );
}

export interface SessionSummary {
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCostUsd: number;
}

export function getSessionSummary(session: Session): SessionSummary {
  return session.messages.reduce(
    (acc, msg) => {
      if (!msg.tokenUsage) return acc;
      const cost = msg.provider && msg.model
        ? estimateCost(msg.provider, msg.model, msg.tokenUsage)
        : 0;
      return {
        totalInputTokens: acc.totalInputTokens + msg.tokenUsage.inputTokens,
        totalOutputTokens: acc.totalOutputTokens + msg.tokenUsage.outputTokens,
        totalCostUsd: acc.totalCostUsd + cost,
      };
    },
    { totalInputTokens: 0, totalOutputTokens: 0, totalCostUsd: 0 },
  );
}

const STORAGE_KEY = "cowork:sessions";
const ACTIVE_KEY = "cowork:activeSessionId";

function loadSessions(): Session[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Session[]) : [];
  } catch {
    return [];
  }
}

function saveSessions(sessions: Session[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
  } catch {
    // Storage quota exceeded — silently ignore
  }
}

export function useChatStore() {
  const [sessions, setSessions] = useState<Session[]>(() => loadSessions());
  const [activeSessionId, setActiveSessionId] = useState<string | null>(
    () => localStorage.getItem(ACTIVE_KEY),
  );
  const [streaming, setStreaming] = useState(false);
  const [partialText, setPartialText] = useState("");
  const [lastError, setLastError] = useState<string | null>(null);
  const [pendingFileCards, setPendingFileCards] = useState<{ filePath: string; fileName: string; fileType: string }[]>([]);
  // Track tool calls visible during streaming (Reasoning Glimpse pattern)
  const [activeToolCall, setActiveToolCall] = useState<string | null>(null);

  // Capture the current provider/model at send time for the assistant message
  const pendingRef = useRef<{ provider: ProviderName; model: string } | null>(null);
  // Capture token usage from the "done" delta
  const pendingUsageRef = useRef<TokenUsage | null>(null);
  // Mirror partialText in a ref so stream_done can read it without a nested state updater
  // (nested setSessions inside setPartialText updater causes double-fire in React Strict Mode)
  const partialTextRef = useRef("");
  const pendingFileCardsRef = useRef<{ filePath: string; fileName: string; fileType: string }[]>([]);

  // Persist sessions and active ID to localStorage on every change
  useEffect(() => { saveSessions(sessions); }, [sessions]);
  useEffect(() => {
    if (activeSessionId) localStorage.setItem(ACTIVE_KEY, activeSessionId);
    else localStorage.removeItem(ACTIVE_KEY);
  }, [activeSessionId]);

  const activeSession = sessions.find((s) => s.id === activeSessionId) ?? null;

  const newSession = useCallback(
    (provider: ProviderName, model: string): string => {
      const id = crypto.randomUUID();
      const session: Session = {
        id,
        title: "New Chat",
        provider,
        model,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        messages: [],
      };
      setSessions((prev) => [...prev, session]);
      setActiveSessionId(id);
      return id;
    },
    [],
  );

  const sendMessage = useCallback(
    (text: string, provider: ProviderName, model: string, systemPrompt?: string) => {
      const sessionId = activeSessionId ?? newSession(provider, model);
      const userMsg = createMessage("user", text);

      setSessions((prev) =>
        prev.map((s) =>
          s.id === sessionId
            ? {
                ...s,
                // Auto-title from first user message (truncated)
                title:
                  s.messages.length === 0
                    ? text.slice(0, 40) + (text.length > 40 ? "…" : "")
                    : s.title,
                messages: [...s.messages, userMsg],
                updatedAt: new Date().toISOString(),
              }
            : s,
        ),
      );

      setStreaming(true);
      setPartialText("");
      partialTextRef.current = "";
      setActiveToolCall(null);
      setLastError(null);
      setPendingFileCards([]);
      pendingRef.current = { provider, model };
      pendingUsageRef.current = null;

      const session = sessions.find((s) => s.id === sessionId);
      const allMessages = [...(session?.messages ?? []), userMsg];

      engineWS.send({
        type: "chat",
        payload: {
          sessionId,
          messages: allMessages,
          provider,
          model,
          ...(systemPrompt !== undefined && { systemPrompt }),
        },
      });
    },
    [activeSessionId, newSession, sessions],
  );

  const cancelStream = useCallback(() => {
    if (activeSessionId) {
      engineWS.send({ type: "cancel", sessionId: activeSessionId });
    }
  }, [activeSessionId]);

  const regenerateLast = useCallback(
    (provider: ProviderName, model: string) => {
      if (!activeSessionId) return;
      setSessions((prev) =>
        prev.map((s) => {
          if (s.id !== activeSessionId) return s;
          // Drop the last assistant message
          const msgs = s.messages.filter((m) => m.role !== "assistant").concat(
            s.messages.filter((m) => m.role === "assistant").slice(0, -1),
          );
          // Actually just drop last assistant
          const lastAssistant = [...s.messages].reverse().findIndex((m) => m.role === "assistant");
          if (lastAssistant === -1) return s;
          const idx = s.messages.length - 1 - lastAssistant;
          return { ...s, messages: s.messages.slice(0, idx) };
        }),
      );

      const session = sessions.find((s) => s.id === activeSessionId);
      if (!session) return;
      const messagesWithoutLastAssistant = (() => {
        const msgs = [...session.messages];
        for (let i = msgs.length - 1; i >= 0; i--) {
          if (msgs[i]?.role === "assistant") {
            return msgs.slice(0, i);
          }
        }
        return msgs;
      })();

      setStreaming(true);
      setPartialText("");
      partialTextRef.current = "";
      setActiveToolCall(null);
      pendingRef.current = { provider, model };
      pendingUsageRef.current = null;

      engineWS.send({
        type: "chat",
        payload: {
          sessionId: activeSessionId,
          messages: messagesWithoutLastAssistant,
          provider,
          model,
        },
      });
    },
    [activeSessionId, sessions],
  );

  // Handle WebSocket messages
  useEffect(() => {
    const removeHandler = engineWS.addHandler((msg) => {
      if (msg.type === "stream_delta" && msg.sessionId === activeSessionId) {
        const { delta } = msg;
        if (delta.type === "text" && delta.text) {
          partialTextRef.current += delta.text;
          setPartialText(partialTextRef.current);
          setActiveToolCall(null);
        }
        // Retract: wipe pre-tool narration text from the current turn
        if (delta.type === "retract") {
          partialTextRef.current = "";
          setPartialText("");
        }
        if (delta.type === "tool_use" && delta.toolName) {
          setActiveToolCall(delta.toolName);
        }
        if (delta.type === "file_created" && delta.filePath && delta.fileName) {
          const card = { filePath: delta.filePath!, fileName: delta.fileName!, fileType: delta.fileType ?? "file" };
          pendingFileCardsRef.current = [...pendingFileCardsRef.current, card];
          setPendingFileCards(pendingFileCardsRef.current);
        }
        if (delta.type === "done" && delta.usage) {
          pendingUsageRef.current = delta.usage;
        }
      }

      if (msg.type === "stream_done" && msg.sessionId === activeSessionId) {
        const text = partialTextRef.current;
        const pending = pendingRef.current;
        const usage = pendingUsageRef.current ?? undefined;
        const fileCards = pendingFileCardsRef.current;

        // Commit the streamed text as an assistant message — read from ref,
        // not from a setPartialText updater, to avoid React Strict Mode double-fire
        if (text) {
          setSessions((prev) =>
            prev.map((s) =>
              s.id === activeSessionId
                ? {
                    ...s,
                    messages: [
                      ...s.messages,
                      createMessage(
                        "assistant",
                        text,
                        pending?.provider,
                        pending?.model,
                        usage,
                        fileCards,
                      ),
                    ],
                    updatedAt: new Date().toISOString(),
                  }
                : s,
            ),
          );
        }

        partialTextRef.current = "";
        pendingFileCardsRef.current = [];
        setPartialText("");
        setPendingFileCards([]);
        setStreaming(false);
        setActiveToolCall(null);
        pendingRef.current = null;
        pendingUsageRef.current = null;
      }

      if (msg.type === "error") {
        setStreaming(false);
        setPartialText("");
        setActiveToolCall(null);
        setLastError(msg.message ?? "Unknown error from engine");
      }
    });

    return removeHandler;
  }, [activeSessionId]);

  return {
    sessions,
    activeSession,
    activeSessionId,
    streaming,
    partialText,
    lastError,
    activeToolCall,
    newSession,
    sendMessage,
    cancelStream,
    regenerateLast,
    setActiveSessionId,
  };
}
